import React, {ReactNode} from 'react';
import {ClientProfile, PatientPhoneNumber as PatientPhoneNumberDto} from "@/api/dto/client-profile";
import {StyledDemographicComponent, StyledDemographicLink} from "@clients/profile_page/ClientDemographicsNode.styles";
import DisplayHelpers from "@/helpers/display_helpers";
import RiskIndicators, {RiskFlagsStyle} from "@clients/RiskIndicators";
import ClientDemographicsNode from "@clients/profile_page/ClientDemographicsNode";
import ClientDemographicsEdit from "@clients/profile_page/ClientDemographicsEdit";
import PatientPhoneNumber from "@common/PatientPhoneNumber";
import helpers from "@/helpers/helpers";
import ToolTip from "patient-ping-remedy/packages/tool-tip";
import {StyledIcon} from "@clients/profile_page/summary_tab/timeline_components/EncounterDischargeInfo.styles";
import {ICONS} from "patient-ping-remedy/packages/icon";
import {colors} from "patient-ping-remedy/packages/theme";
import {SearchParam} from "@/helpers/search_params";

type ModalProps = {
  patient?: ClientProfile;
  isEditAvailable?: boolean;
  onEdit?(): void;
  linkName?: boolean;
}

/**
 * Returns the phone to display in the header: the server-computed best dialable number
 * (CARECO-6135), so what's shown matches what the campaign dials. The matching entry from
 * patientPhoneNumbers is reused when found so its type (e.g. 'mobile') still drives the '(cell)'
 * indicator; otherwise a minimal entry carrying just the number is returned. Returns undefined
 * when the server resolved no valid number, so the renderer falls back to '-'.
 */
const getBestPhoneNumber = (profile?: ClientProfile): PatientPhoneNumberDto | undefined => {
  const best = profile?.bestPhoneNumber;
  if (!best) {
    return undefined;
  }
  const bestDigits = best.replace(/\D/g, '');
  const matched = profile?.patientPhoneNumbers?.find(
    (entry) => entry?.phone?.replace(/\D/g, '').endsWith(bestDigits.slice(-10))
  );
  return matched ?? ({phone: best} as PatientPhoneNumberDto);
};

const ClientDemographicHeader = (props: ModalProps): ReactNode[] => {
  // React Compiler thinks this function is a component,
  // trying to memoize it throws an error so we disable this option
  'use no memo';

  const headerNodes: ReactNode[] = [];
  const patient = props.patient?.patient;
  const age = patient?.dateOfBirth?  helpers.getAge(patient.dateOfBirth) : null;
  const isMinor = typeof age === 'number' && age < 18;

  if (!patient) {
    return headerNodes;
  }

  const fullName = DisplayHelpers.formatName(patient.firstName, patient.middleName, patient.lastName);
  const nameSuffix = props.patient?.extendedDemographics?.nameSuffix;
  const displayName = nameSuffix ? `${fullName} ${nameSuffix}` : fullName;
  const nameHref = props.linkName
    ? `/clients/profile?${SearchParam.PATIENT_ID}=${patient.patientId}&${SearchParam.ROSTER_ID}=${patient.creatorGroupId}`
    : undefined;

  headerNodes.push(
    <StyledDemographicComponent
      key="name"
      fontSize={"2rem"}
      lineHeight={"2.4rem"}
      whiteSpace={"nowrap"}
    >
      {nameHref
        ? <StyledDemographicLink to={nameHref} onClick={(event) => event.stopPropagation()}>{displayName}</StyledDemographicLink>
        : displayName}
    </StyledDemographicComponent>
  );
  headerNodes.push(
    <ClientDemographicsNode
      key="dob"
      label="Date of birth"
      value={DisplayHelpers.formatDateOfBirthWithAge(patient.dateOfBirth) || '-'}
      labelChildren={isMinor && (
        <ToolTip text="Client is under the age of 18" type="dark">
          <span>
            <StyledIcon
              iconClass={ICONS['exclamation-triangle']}
              color={colors.red5}
              iconSize="1x"
              weight="fas"
            />
          </span>
        </ToolTip>
      )}
    />
  );

  headerNodes.push(
    <ClientDemographicsNode
      key="ssn"
      label="Social security number"
      value={DisplayHelpers.formatSSN(patient.ssn) || '-'}
    />
  );

  headerNodes.push(
    <ClientDemographicsNode
      key="gender"
      label="Gender"
      value={DisplayHelpers.genderFromEnum(patient.gender) || '-'}
    />
  );


  headerNodes.push(
    <ClientDemographicsNode
      key="clientId"
      label="Client ID"
      value={!patient.clientId ? '-' : patient.clientId}
    />
  );

  headerNodes.push(
    <ClientDemographicsNode
      key="phone"
      label="Phone"
      value={<PatientPhoneNumber phoneNumber={getBestPhoneNumber(props.patient)} />}
    />
  );

  headerNodes.push(
    <ClientDemographicsNode
      key="address"
      label="Address"
      value={DisplayHelpers.formatAddress(patient.address) || '-'}
    />
  );

  headerNodes.push(
    <RiskIndicators
      key="risk-flags"
      riskFlags={patient.unrestrictedPatientFlags}
      styleType={RiskFlagsStyle.SECONDARY}
    />
  );
    
  headerNodes.push(
    <ClientDemographicsNode
      key="insurances"
      label="Insurance on file"
      value={props.patient?.patientRosterInsurances[0]?.insurances[0] || '-'}
    />
  );

  if(props.patient) {
    headerNodes.push(
      <ClientDemographicsEdit
        key="edit"
        isEditAvailable={props.isEditAvailable}
        onClick={props.onEdit}
        patientId={props.patient?.patient.patientId}
      />
    );
  }

  return headerNodes;
};

export default ClientDemographicHeader;
